/**
 * @file ProductReviewCard.tsx
 * @description Карточка клиентского отзыва в очереди модерации (phase19.10).
 *
 * Только чтение содержимого: флаги стоп-листа, оценка, автор, товар, цитата,
 * фото, ответ магазина. Текст отзыва показывается цитатой и не редактируется
 * (R3 docs/business/reviews.md), поля ввода текста здесь нет.
 */

import { Star } from "lucide-react";
import { Link } from "react-router";

import { Badge } from "../../../components/ui/badge";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import {
  REVIEW_FLAG_LABEL,
  REVIEW_STATUS_LABEL,
  REVIEW_STATUS_TONE,
  type AdminProductReview,
  type ProductReviewStatus,
} from "../../../lib/api-product-reviews";
import { ProductReviewActions } from "./ProductReviewActions";

/** Цвет левой полосы карточки по статусу отзыва. */
const STATUS_STRIPE: Record<ProductReviewStatus, string> = {
  PENDING: "var(--warning)",
  PUBLISHED: "var(--primary)",
  REJECTED: "var(--destructive)",
  HIDDEN: "var(--outline-variant)",
};

export function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} из 5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={
            i < rating
              ? "size-3.5 fill-warning text-warning"
              : "size-3.5 text-on-surface-variant"
          }
        />
      ))}
    </span>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface ProductReviewCardProps {
  review: AdminProductReview;
  shopSlug: string;
  onChanged: () => void;
}

export function ProductReviewCard({ review, shopSlug, onChanged }: ProductReviewCardProps) {
  const status = review.status as ProductReviewStatus;

  return (
    <EditorialCard
      className="overflow-hidden"
      style={{ borderLeft: `4px solid ${STATUS_STRIPE[status] ?? "var(--outline-variant)"}` }}
    >
      <EditorialCard.Body className="flex flex-col gap-3">
        {/* Флаги стоп-листа + статус + дата */}
        <div className="flex flex-wrap items-center gap-2">
          {review.flags.map((flag) => (
            <Badge key={flag} variant="destructive" className="text-[10px]">
              ⚠ {REVIEW_FLAG_LABEL[flag] ?? flag}
            </Badge>
          ))}
          <Badge variant={REVIEW_STATUS_TONE[status] ?? "default"} className="text-[10px]">
            {REVIEW_STATUS_LABEL[status] ?? review.status}
          </Badge>
          {review.isEdited ? (
            <Badge variant="outline" className="text-[10px]">
              Изменён автором
            </Badge>
          ) : null}
          <span className="ml-auto text-xs text-on-surface-variant tabular-nums">
            {formatDateTime(review.createdAt)}
          </span>
        </div>

        {/* Оценка · автор · товар */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <Stars rating={review.rating} />
          <Link
            to={`/shop/${shopSlug}/customers/${review.author.customerId}`}
            className="font-medium text-primary hover:underline"
          >
            {review.author.name}
          </Link>
          <span className="text-on-surface-variant">→</span>
          <Link
            to={`/shop/${shopSlug}/catalog/products/${review.product.id}`}
            className="text-primary hover:underline"
          >
            {review.product.name}
          </Link>
          {review.isVerified ? (
            <Badge variant="primary" className="text-[10px]">
              ✓ Купил здесь
            </Badge>
          ) : null}
          <span className="text-xs text-on-surface-variant">
            заказов: {review.author.ordersCount}
          </span>
        </div>

        {/* Текст — только цитата (R3) */}
        <blockquote className="whitespace-pre-wrap break-words border-l-2 border-outline-variant pl-3 text-sm text-on-surface">
          «{review.body}»
        </blockquote>

        {review.photos.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {review.photos.map((p) => (
              <a key={p.id} href={p.url} target="_blank" rel="noreferrer">
                <img
                  src={p.url}
                  alt="Фото к отзыву"
                  loading="lazy"
                  className="size-20 rounded-md object-cover"
                />
              </a>
            ))}
          </div>
        ) : null}

        {review.rejectReason ? (
          <p className="text-xs text-destructive">
            Причина отклонения: {review.rejectReason}
          </p>
        ) : null}

        {review.shopReply ? (
          <div className="rounded-lg bg-surface-low p-3 text-sm">
            <div className="text-xs uppercase tracking-wide text-on-surface-variant">
              Ответ магазина
            </div>
            <p className="mt-1 whitespace-pre-wrap break-words">{review.shopReply}</p>
          </div>
        ) : null}

        <ProductReviewActions review={review} onChanged={onChanged} />
      </EditorialCard.Body>
    </EditorialCard>
  );
}
